import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Flex,
  Stack,
  useMediaQuery,
  useTheme,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import shoplineCaptcha from '../../../../utils/shoplineCaptcha';
import { createDeferredPromise } from '../../../../utils/promise';
import { useAppContext } from '../../context/appContext';
import { userPropTypes } from '../../utils/propTypes';
import EInvoiceForm from './components/EInvoiceForm';
import { useProfileContext } from './context/profileContext';
import ContactInfoSection from './sections/ContactInfoSection';
import MemberDataSection from './sections/MemberDataSection';
import MemberInfoReward from './sections/MemberInfoReward';
import MemberReferral from './sections/MemberReferral';
import MemberReminder from './sections/MemberReminder';
import SubscriptionSection from './sections/SubscriptionSection';
import getSchema from './schema';
import * as utils from './utils';

const CONFIRM_BUTTON_ID = 'member-center-profile-confirm_button';

const Profile = (props) => {
  const { cachedData, onSubmit } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const [isDesktop] = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const { slFeatureService, userSetting } = useAppContext();
  const { userData } = useProfileContext();
  const [addressSchemaMap, setAddressSchemaMap] = useState({});
  const methods = useForm({
    defaultValues:
      cachedData ??
      utils.transformUserDataToFormData({
        userData,
        userSetting,
      }),
    resolver: yupResolver(
      getSchema({
        userData,
        userSetting,
        addressSchemaMap,
        hasCountryCode: slFeatureService.hasFeature('country_code'),
      }),
    ),
    shouldUnregister: false,
  });
  const hasShoplineEmailCaptcha =
    slFeatureService.hasFeature('shopline_captcha') &&
    slFeatureService.hasFeature('shopline_email_captcha');
  const shoplineEmailCaptchaPromiseRef = useRef(createDeferredPromise());

  const checkShoplineCaptchaOnSubmit = async (formData) => {
    if (hasShoplineEmailCaptcha) {
      onSubmit(formData, {
        m_token: await shoplineEmailCaptchaPromiseRef.current.promise,
        dfp_token: await shoplineCaptcha.getTokenAsync(),
      });
    } else {
      onSubmit(formData);
    }
  };

  useEffect(() => {
    if (userData && methods.formState.isDirty) {
      methods.reset(
        utils.transformUserDataToFormData({ userData, userSetting }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    if (hasShoplineEmailCaptcha) {
      const listener = shoplineCaptcha.getListener(
        CONFIRM_BUTTON_ID,
        (token) => {
          shoplineEmailCaptchaPromiseRef.current.resolve(token);
        },
      );
      return () => listener?.destroy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () =>
    methods.reset(utils.transformUserDataToFormData({ userData, userSetting }));

  return (
    <>
      <FormProvider {...methods}>
        <Box
          as="form"
          id="member-center-profile__form"
          onSubmit={methods.handleSubmit(checkShoplineCaptchaOnSubmit)}
          pb="10px"
        >
          <Flex
            direction="column"
            gap={isDesktop ? '1.875rem' : '0px'}
            pb="30px"
          >
            <MemberReminder />
            <Flex direction={isDesktop ? 'row' : 'column'} gap="1.875rem">
              <MemberInfoReward />
              <MemberReferral />
            </Flex>
          </Flex>
          <Stack
            direction={isDesktop ? 'row' : 'column'}
            spacing="20px"
            alignItems="flex-start"
          >
            <MemberDataSection />
            <Flex direction="column" gap="20px" w={isDesktop ? '50%' : '100%'}>
              <SubscriptionSection />
              <ContactInfoSection setAddressSchemaMap={setAddressSchemaMap} />
            </Flex>
          </Stack>
          <Flex
            justifyContent={isDesktop ? 'flex-end' : 'space-between'}
            alignItems="center"
            gap="8px"
            mt={isDesktop ? '18px' : '30px'}
          >
            <Button
              type="button"
              className="member-center-profile__default-button"
              width={isDesktop ? 'auto' : '100%'}
              onClick={handleCancel}
              data-e2e-id="member-center-profile-cancel_button"
            >
              {t('member_profile.cancel')}
            </Button>
            <Button
              variant="fill"
              type="submit"
              width={isDesktop ? 'auto' : '100%'}
              className="member-center-profile__submit-button member-center-profile__primary-button"
              data-e2e-id="member-center-profile-confirm_button"
              id={CONFIRM_BUTTON_ID}
            >
              {t('member_profile.confirm')}
            </Button>
          </Flex>
        </Box>
      </FormProvider>
      <EInvoiceForm />
    </>
  );
};

Profile.propTypes = {
  cachedData: userPropTypes,
  onSubmit: PropTypes.func.isRequired,
};

export default Profile;
